import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SignUp from '../components/SignUp';
import FormHeader from '../components/FormHeader';
import InvestorSignupContent from '../components/signup/InvestorSignupContent';
import prairiwhite from '../assets/prairiwhite.png';
import { DEFAULT_INVESTOR_PLAN } from '../constants/investorPricingPlans';
import { loadPricingSignupDraft } from '../utils/pricingSignupDraft';

interface SignupPageProps {
  userType: 'startup' | 'investor';
}

/** utm_source=founders-live → "Founders Live" */
function formatPartnerGreetingName(source: string): string {
  return source
    .trim()
    .replace(/[-_]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const SignupPage: React.FC<SignupPageProps> = ({ userType }) => {
  const location = useLocation();
  const [pricingType, setPricingType] = useState<'yearly' | 'lifetime' | 'threeYear'>(() => {
    const draft = loadPricingSignupDraft(userType);
    if (draft?.pricingType) return draft.pricingType;
    return userType === 'investor' ? DEFAULT_INVESTOR_PLAN : 'lifetime';
  });
  const [utmSource, setUtmSource] = useState<string | null>(null);
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(false);

  // SignupPage is reused across /investors/pricing and /startups/pricing — reset plan when userType changes.
  useEffect(() => {
    const draft = loadPricingSignupDraft(userType);
    setPricingType(
      draft?.pricingType ?? (userType === 'investor' ? DEFAULT_INVESTOR_PLAN : 'lifetime')
    );
  }, [userType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = params.get('utm_source');
    if (source) setUtmSource(source);
  }, [location.search]);

  // Investor — native plan cards + signup form
  if (userType === 'investor') {
    return (
      <InvestorSignupContent
        pricingType={pricingType}
        onPricingTypeChange={setPricingType}
        onPromoCodeValid={setIsPromoCodeValid}
      />
    );
  }

  // Startup — existing hardcoded layout (unchanged)
  return (
    <div className="min-h-screen bg-dark-950">
      <div className="flex flex-col lg:flex-row justify-center items-start max-w-7xl mx-auto">
        {/* Form Column */}
        <div className="w-full lg:w-1/2 flex items-start justify-center p-4 sm:p-6 lg:px-12 lg:py-8 order-1 lg:order-2">
          <div className="w-full max-w-lg">
            <div className="bg-black/50 backdrop-blur-sm rounded-3xl p-5 sm:p-6 lg:p-10 shadow-xl border border-gray-700/30">
              <FormHeader title="Join PRAIRI as a Startup" subtitle="" compact />
              <div className="mt-1">
                <SignUp
                  userType={userType}
                  pricingType={pricingType}
                  onPromoCodeValid={isValid => setIsPromoCodeValid(isValid)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Column */}
        <div className="w-full lg:w-1/2 flex flex-col justify-start p-4 sm:p-6 lg:px-12 lg:py-8 order-2 lg:order-1 bg-dark-950">
          <div className="max-w-2xl">
            <div className="mb-8">
              <div className="flex items-center gap-4">
                {utmSource && (
                  <div className="flex-shrink-0">
                    <img src={prairiwhite} alt="Prairi" className="w-12 h-12 object-contain" />
                  </div>
                )}
                <div>
                  {utmSource && (
                    <h2 className="text-2xl lg:text-3xl font-bold text-emerald-400 mb-2 text-left">
                      Hey, {formatPartnerGreetingName(utmSource)} member!
                    </h2>
                  )}
                  <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight text-left">
                    Match with investors looking for a startup like yours
                  </h1>
                </div>
              </div>
            </div>

            <div className="mb-8 bg-transparent border border-blue-500 rounded-lg overflow-hidden">
              <div className="px-6 py-3 text-white font-semibold bg-gradient-to-r from-blue-500 to-blue-600">
                Unlimited Funding Rounds
              </div>
              <div className="p-6 text-left">
                <div className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-1">
                  lifetime access
                </div>
                <div className="flex items-baseline gap-3 mb-2">
                  <div
                    className={`text-white text-5xl font-bold ${isPromoCodeValid ? 'line-through decoration-red-500 decoration-4' : ''}`}
                  >
                    $149
                  </div>
                  {isPromoCodeValid && <div className="text-white text-5xl font-bold">FREE</div>}
                </div>
                <div className="text-gray-400 text-sm mb-6 text-left">one-time payment</div>
                <ul className="space-y-3">
                  {[
                    'Raise additional rounds (avoiding new join fees)',
                    'Be seen by local and global investors',
                    'Match with investors - then they contact YOU',
                    'Track your deal progress',
                    'Less Chasing. More Building',
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-white text-sm">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="mt-0.5 flex-shrink-0"
                      >
                        <path
                          d="M6 10L9 13L14 7"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
